import axios, { AxiosResponse } from 'axios';
import { getConfig } from '../config';
import { Question } from '../database/theSource';

const BASE_URL = getConfig().api.baseUrl;
export interface TheSourceApiContextType {
    /**
     * Creates a question.
     * @param req The CreateQuestionRequest.
     * @returns The newly created question record.
     */
    createQuestion: (req: CreateQuestionRequest) => Promise<AxiosResponse<Question>>;

    /**
     * listQuestionsByIsPrivate returns a list of most recent public or private questions.
     * @param startKey The optional startKey to use when searching.
     * @returns A list of the most recent questions.
     */
    listQuestionsByIsPrivate: (
        isPrivate: string,
        startKey?: string,
    ) => Promise<AxiosResponse<ListQuestionsResponse>>;

    /**
     * listQuestionsByOwner returns a list of the current user's questions.
     * @param startKey The optional startKey to use when searching.
     * @returns A list of the most recent questions.
     */
    listQuestionsByOwner: (
        startKey?: string,
    ) => Promise<AxiosResponse<ListQuestionsResponse>>;

    /**
     * listQuestionsByResponseType returns a list of questions for a specific requested response type.
     * @param startKey The optional startKey to use when searching.
     * @returns A list of the most recent questions.
     */
    listQuestionsByResponseType: (
        responseType: string,
        startKey?: string,
    ) => Promise<AxiosResponse<ListQuestionsResponse>>;

    /**
     * listQuestionsByStatus returns a list of questions for a specific status.
     * @param startKey The optional startKey to use when searching.
     * @returns A list of the most recent questions for the specified status.
     */
    listQuestionsByStatus: (
        status: string,
        scanForwardIndex: boolean,
        startKey?: string,
    ) => Promise<AxiosResponse<ListQuestionsResponse>>;

    /**
     * answerQuestion updates a question with the artist's answer.
     * @param req The AnswerQuestionRequest.
     * @returns The updated question.
     */
    answerQuestion: (req: AnswerQuestionRequest) => Promise<AxiosResponse<Question>>;

    theSourceGeneratePresignedUrl: (
        question: Question,
        contentType: string,
    ) => Promise<AxiosResponse<PresignedUrlResponse>>;

    uploadVideoToS3: (req: MediaUploadRequest) => Promise<AxiosResponse<string>>;

    /**
     * deleteQuestion deletes the provided question in the database.
     * @param question The Question.
     * @returns The deleted question.
     */
    deleteQuestion: (question: Question) => Promise<AxiosResponse<Question, any>>;

    /**
     * setQuestionLike sets a like on a question.
     * If the user has already liked the question, the like will be removed.
     * If the user has not liked the question, a like will be added.
     * @param owner The owner of the question.
     * @param createdAt The creation date of the question.
     * @param req SetLikeRequest that contains if question is liked
     * @returns The updated question.
     */
    setQuestionLike: (
        owner: string,
        createdAt: string,
        req: SetLikeRequest,
    ) => Promise<AxiosResponse<Question, any>>;
}

export interface CreateQuestionRequest {
    content: string;
    responseType: string;
    isAcknowledged: boolean;
    isPrivate: string;
    selectedPrice?: number;
}

export interface SetUpFuturePaymentsRequest {
    content: string;
    responseType: string;
    isAcknowledged: boolean;
    isPrivate: string;
    selectedPrice?: number;
}

export interface AnswerQuestionRequest {
    questionOwner: string;
    questionCreatedAt: string;
    content: string;
    responseType: string;
    isPrivate: string;
}

export interface ListQuestionsResponse {
    questions: Question[];
    lastEvaluatedKey?: string;
}

export interface PresignedUrlResponse {
    presignedUrl: string;
    key: string;
}

export interface MediaUploadRequest {
    presignedUrl: string;
    contentType: string;
    mediaContent: File;
    onUploadProgress?: (progressEvent: ProgressEvent) => void;
}

export interface SetLikeRequest {
    isLiked: boolean;
}

export function createQuestion(idToken: string, req: CreateQuestionRequest) {
    return axios.post<Question>(`${BASE_URL}/the-source/question`, req, {
        headers: {
            Authorization: 'Bearer ' + idToken,
        },
    });
}

export async function listQuestionsByIsPrivate(isPrivate: string, startKey?: string) {
    return axios.get<ListQuestionsResponse>(
        BASE_URL + `/public/the-source/recent/${isPrivate}`,
        {
            params: { startKey },
        },
    );
}

export async function listQuestionsByOwner(idToken: string, startKey?: string) {
    return axios.get<ListQuestionsResponse>(BASE_URL + '/the-source/my-questions', {
        params: { startKey },
        headers: {
            Authorization: 'Bearer ' + idToken,
        },
    });
}

export async function listQuestionsByResponseType(
    responseType: string,
    idToken: string,
    startKey?: string,
) {
    return axios.get<ListQuestionsResponse>(
        BASE_URL + `/the-source/questions/${responseType}`,
        {
            params: { startKey },
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export async function listQuestionsByStatus(
    status: string,
    scanForwardIndex: boolean,
    idToken: string,
    startKey?: string,
) {
    return axios.get<ListQuestionsResponse>(
        BASE_URL + `/the-source/questions/status/${status}`,
        {
            params: { startKey, scanForwardIndex },
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export function answerQuestion(idToken: string, req: AnswerQuestionRequest) {
    return axios.put<Question>(`${BASE_URL}/the-source/question/answer`, req, {
        headers: {
            Authorization: 'Bearer ' + idToken,
        },
    });
}

export function theSourceGeneratePresignedUrl(
    idToken: string,
    question: Question,
    contentType: string,
) {
    return axios.post(
        `${BASE_URL}/the-source/question/presign-url`,
        { question, contentType },
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export function uploadVideoToS3(req: MediaUploadRequest) {
    return axios.put(req.presignedUrl, req.mediaContent, {
        headers: {
            'Content-Type': req.contentType,
        },
    });
}

export function deleteQuestion(idToken: string, question: Question) {
    return axios.delete(
        `${BASE_URL}/the-source/question/delete/${question.owner}/${question.createdAt}`,
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export function setQuestionLike(
    idToken: string,
    questionOwner: string,
    questionCreatedAt: string,
    req: SetLikeRequest,
) {
    return axios.put<Question>(
        `${BASE_URL}/the-source/${questionOwner}/${questionCreatedAt}/like`,
        req,
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}
